import { getUserRoleDescription, getUserRolesLabel } from "components/MemberList/userRoles";
import { ROLES } from "containers/ScopeContext/roles.enum";
import { useEffect, useMemo } from "react";
import { fetchRoles } from "store/permissionsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import { useCompanySettings } from "./useCompanySettings";

export type PossibleUserRole = {
    role: ROLES;
    label: string;
    description: string;
    isCustom: boolean;
    id?: number;
};

/** Default roles, broadest access first. Custom roles stay after this list. */
const DEFAULT_ROLE_PRIORITY: ROLES[] = [
    ROLES.OWNER,
    ROLES.RIGHTS_MANAGER,
    ROLES.ACCOUNTANT,
    ROLES.VIEW_ONLY,
    ROLES.AGREEMENT_ADMIN,
    ROLES.AGREEMENT_MANAGER,
    ROLES.AGREEMENT_CONTRIBUTOR,
];

const defaultRolePriority = new Map(DEFAULT_ROLE_PRIORITY.map((role, index) => [role, index]));

const compareRolesByPriority = (left: PossibleUserRole, right: PossibleUserRole) => {
    const leftRank = left.isCustom
        ? Number.POSITIVE_INFINITY
        : defaultRolePriority.get(left.role) ?? Number.MAX_SAFE_INTEGER;
    const rightRank = right.isCustom
        ? Number.POSITIVE_INFINITY
        : defaultRolePriority.get(right.role) ?? Number.MAX_SAFE_INTEGER;
    return leftRank - rightRank;
};

/**
 * Roles offered when adding a user: company roles, without "none" and stock manager,
 * and without agreement roles when the company does not use agreements.
 */
export const usePossibleUserRoles = () => {
    const dispatch = useAppDispatch();
    const { getCompanySettings } = useCompanySettings();
    const usesAgreements = getCompanySettings("uses_agreements") ?? false;
    const { roles: companyRoles, rolesFetchStatus } = useAppSelector((state) => state.permissions);

    useEffect(() => {
        if (rolesFetchStatus === "idle") {
            dispatch(fetchRoles());
        }
    }, [dispatch, rolesFetchStatus]);

    const possibleUserRoles = useMemo(() => {
        const rolesToExclude: ROLES[] = [ROLES.NONE, ROLES.STOCK_MANAGER];

        if (!usesAgreements) {
            rolesToExclude.push(ROLES.AGREEMENT_ADMIN, ROLES.AGREEMENT_MANAGER, ROLES.AGREEMENT_CONTRIBUTOR);
        }

        return companyRoles
            .filter((role) => !rolesToExclude.includes(role.name as ROLES))
            .map((role) => ({
                role: role.name as ROLES,
                label: getUserRolesLabel(role.name as ROLES) ?? role.name,
                description: role.description || getUserRoleDescription(role.name as ROLES) || "",
                isCustom: role.isCustom,
                id: role.id,
            }))
            .sort(compareRolesByPriority);
    }, [companyRoles, usesAgreements]);

    return { possibleUserRoles, rolesFetchStatus };
};
